import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  ChevronDown, 
  Menu, 
  X,
  ChevronRight,
  User,
  LogOut,
  Linkedin,
  Twitter,
  Facebook
} from 'lucide-react';
import CRMONCELogo from './CRMONCELogo';

interface UserData {
  fullName: string;
  email: string;
  id: string;
}

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check for logged in user on component mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    
    if (storedUser && isLoggedIn === 'true') {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('isLoggedIn');
      }
    }

    // Listen for login events
    const handleLogin = () => {
      const storedUser = localStorage.getItem('user');
      const isLoggedIn = localStorage.getItem('isLoggedIn');
      
      if (storedUser && isLoggedIn === 'true') {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    };

    // Listen for logout events
    const handleLogoutEvent = () => {
      setUser(null);
      setShowUserMenu(false);
    };

    // Add event listeners
    window.addEventListener('userLogin', handleLogin);
    window.addEventListener('userLogout', handleLogoutEvent);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('userLogin', handleLogin);
      window.removeEventListener('userLogout', handleLogoutEvent);
    };
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    setUser(null);
    setShowUserMenu(false);
    
    // Dispatch logout event
    window.dispatchEvent(new Event('userLogout'));
    
    navigate('/');
  };

  // Throttled scroll handler to prevent glitches
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    // Only update if scroll difference is significant (prevents micro-movements)
    if (Math.abs(currentScrollY - lastScrollY) < 3) return;
    
    // Show header when scrolling up, hide when scrolling down
    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      // Scrolling down - hide header
      setIsHeaderVisible(false);
    } else if (currentScrollY < lastScrollY || currentScrollY < 80) {
      // Scrolling up or near top - show header
      setIsHeaderVisible(true);
    }
    
    // Update scroll state for background
    setIsScrolled(currentScrollY > 50);
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  // Reset header visibility when location changes
  useEffect(() => {
    // Always show header when navigating to a new page
    setIsHeaderVisible(true);
    setIsScrolled(false);
    setLastScrollY(0);
    
    // Scroll to top when navigating to a new page
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Debounced scroll handler to ensure header shows when scrolling stops
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    
    const handleScrollEnd = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Show header after scrolling stops for 150ms
        setIsHeaderVisible(true);
      }, 150);
    };
    
    window.addEventListener('scroll', handleScrollEnd, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScrollEnd);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showUserMenu && !target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    let ticking = false;
    let lastScrollTime = 0;

    const throttledScrollHandler = () => {
      const now = Date.now();
      if (!ticking && now - lastScrollTime > 10) { // Throttle to 10ms minimum
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
        lastScrollTime = now;
      }
    };

    // Use passive listener for better performance
    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    // Also listen for wheel events for more responsive behavior
    const wheelHandler = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > 3) {
        throttledScrollHandler();
      }
    };
    window.addEventListener('wheel', wheelHandler, { passive: true });
    
    // Add touch events for mobile devices
    let touchStartY = 0;
    const touchStartHandler = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    
    const touchMoveHandler = (e: TouchEvent) => {
      const touchY = e.touches[0].clientY;
      const deltaY = touchStartY - touchY;
      
      if (Math.abs(deltaY) > 10) {
        throttledScrollHandler();
      }
    };
    
    window.addEventListener('touchstart', touchStartHandler, { passive: true });
    window.addEventListener('touchmove', touchMoveHandler, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      window.removeEventListener('wheel', wheelHandler);
      window.removeEventListener('touchstart', touchStartHandler);
      window.removeEventListener('touchmove', touchMoveHandler);
    };
  }, [handleScroll]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { 
      name: 'Services', 
      path: '/services',
      submenu: [
        { name: 'Dynamics 365 Implementation', path: '/dynamics365-implementation', icon: '🏢' },
        { name: 'Power Platform Development', path: '/power-platform-development', icon: '⚡' },
        { name: 'Consulting Services', path: '/consulting-services', icon: '👥' },
        { name: 'Professional Training', path: '/professional-training', icon: '🎓' },
        { name: 'Features', path: '/features', icon: '✨' },
      ]
    },
                  { name: 'Add-ons', path: '/add-ons' },
              { name: 'Pricing', path: '/pricing' },
              { name: 'Support', path: '/support' },
              { name: 'Blog', path: '/blog' },
              { 
                name: 'Contact', 
                path: '/contact',
                submenu: [
                  { name: 'Contact Us', path: '/contact', icon: '📞' },
                  { name: 'About Us', path: '/about', icon: 'ℹ️' },
                ]
              },
  ];

  return (
    <AnimatePresence mode="wait">
      {isHeaderVisible && (
        <motion.header
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          exit={{ y: -100 }}
          transition={{ 
            duration: 0.3, 
            ease: [0.25, 0.1, 0.25, 1] // Faster, more responsive easing
          }}
                     className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
             isScrolled 
               ? 'bg-white shadow-lg border-b border-gray-200' 
               : 'bg-white'
           }`}
        >
                     {/* Top Contact Bar */}
           <motion.div 
             initial={{ opacity: 0, y: -20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.1, duration: 0.3 }}
                           className="bg-white border-b border-gray-100 py-1"
           >
                           <div className="container-custom">
                <div className="flex items-center justify-between py-1 text-sm max-w-6xl mx-auto">
                                   {/* Contact Information - Desktop */}
                  <div className="hidden md:flex items-center space-x-6">
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-gray-600" />
                      <span className="text-gray-700">Phone: +91 8096556344</span>
                    </div>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-gray-700">Email: info@crmonce.com</span>
                    </div>
                  </div>
                 
                                   {/* Contact Information - Mobile */}
                  <div className="md:hidden flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <Phone className="w-3 h-3 text-gray-600" />
                      <span className="text-gray-700 text-xs">+91 8096556344</span>
                    </div>
                    <div className="w-px h-3 bg-gray-300"></div>
                    <div className="flex items-center space-x-1">
                      <Mail className="w-3 h-3 text-gray-600" />
                      <span className="text-gray-700 text-xs">info@crmonce.com</span>
                    </div>
                  </div>
                 
                                   {/* Social Media Links */}
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-600 hidden sm:inline">Follow us:</span>
                    <div className="flex items-center space-x-2">
                      <a 
                        href="https://www.linkedin.com/company/crmonce-opc-pvt-ltd/posts/?feedView=all" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center hover:bg-blue-700 transition-colors"
                        aria-label="Follow us on LinkedIn"
                      >
                        <Linkedin className="w-3 h-3 text-white" />
                      </a>
                      <a 
                        href="https://twitter.com/crmonce" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-6 h-6 bg-sky-500 rounded flex items-center justify-center hover:bg-sky-600 transition-colors"
                        aria-label="Follow us on Twitter"
                      >
                        <Twitter className="w-3 h-3 text-white" />
                      </a>
                      <a 
                        href="https://www.facebook.com/crmonce1" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-6 h-6 bg-blue-700 rounded flex items-center justify-center hover:bg-blue-800 transition-colors"
                        aria-label="Follow us on Facebook"
                      >
                        <Facebook className="w-3 h-3 text-white" />
                      </a>
                    </div>
                  </div>
               </div>
             </div>
           </motion.div>

                     {/* Main Navigation */}
                       <motion.nav 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
                             className="py-2 bg-white border-b border-gray-100"
            >
                         <div className="container-custom">
                               <div className="flex items-center justify-between max-w-6xl mx-auto py-1">
                                                 {/* Logo */}
                                 <Link to="/" className="flex items-center">
                   <CRMONCELogo size="lg" variant="full" />
                 </Link>

                                 {/* Desktop Navigation */}
                                   <div className="hidden lg:flex items-center space-x-3 xl:space-x-4 2xl:space-x-6">
                  {navItems.map((item) => (
                    <div key={item.name} className="relative group">
                                             <Link
                         to={item.path}
                         className={`nav-link flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors duration-200 px-2 py-1 ${
                           isActive(item.path) 
                             ? 'text-blue-600 font-semibold' 
                             : ''
                         }`}
                         onClick={() => {
                           // Ensure header is visible when navigating
                           setIsHeaderVisible(true);
                         }}
                       >
                        <span className="text-sm font-medium">{item.name}</span>
                        {item.submenu && <ChevronDown className="w-3 h-3 ml-1" />}
                      </Link>
                      
                      {/* Submenu */}
                      {item.submenu && (
                        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top">
                          <div className="p-4 space-y-2">
                            {item.submenu.map((subItem) => (
                                                             <Link
                                 key={subItem.name}
                                 to={subItem.path}
                                 className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                 onClick={() => {
                                   // Close mobile menu if open
                                   setIsMobileMenuOpen(false);
                                   // Ensure header is visible
                                   setIsHeaderVisible(true);
                                 }}
                               >
                                 <span className="text-lg">{subItem.icon}</span>
                                 <span className="text-gray-700 hover:text-accent-600 transition-colors duration-200">
                                   {subItem.name}
                                 </span>
                               </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                                 {/* CTA Button - Show user info if logged in, otherwise show login/register */}
                                                                       <div className="hidden lg:flex items-center space-x-3">
                                     {user ? (
                     // User is logged in - show user menu
                     <div className="relative user-menu-container">
                       <button
                         onClick={() => setShowUserMenu(!showUserMenu)}
                         className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors duration-200"
                       >
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-medium text-sm">{user.fullName}</span>
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      
                      {/* User Dropdown Menu */}
                      <AnimatePresence>
                        {showUserMenu && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100"
                          >
                            <div className="p-4">
                              <div className="border-b border-gray-100 pb-3 mb-3">
                                <p className="font-semibold text-gray-900">{user.fullName}</p>
                                <p className="text-sm text-gray-600">{user.email}</p>
                              </div>
                              <div className="space-y-2">
                                <Link
                                  to="/dashboard"
                                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-gray-700"
                                  onClick={() => setShowUserMenu(false)}
                                >
                                  <User className="w-4 h-4" />
                                  <span>Dashboard</span>
                                </Link>
                                <button
                                  onClick={handleLogout}
                                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-gray-700 w-full text-left"
                                >
                                  <LogOut className="w-4 h-4" />
                                  <span>Logout</span>
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    // User is not logged in - show login/register buttons
                    <>
                      <Link
                        to="/login"
                        className="text-gray-700 hover:text-blue-600 transition-colors duration-200 text-sm font-medium"
                      >
                        Login
                      </Link>
                                             <Link
                         to="/register"
                                                   className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all duration-300 shadow-md hover:shadow-lg font-medium text-sm"
                       >
                        <div className="flex flex-col items-center">
                          <span>Get</span>
                          <span>Started</span>
                        </div>
                      </Link>
                    </>
                  )}
                </div>

                {/* Mobile Menu Button */}
                                 <button
                   onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                   className="lg:hidden p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                 >
                  {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
              </div>
            </div>
          </motion.nav>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="lg:hidden bg-white border-t border-gray-200"
              >
                <div className="container-custom py-4 space-y-4">
                  {navItems.map((item) => (
                    <div key={item.name}>
                                             <Link
                         to={item.path}
                         className={`block py-2 ${
                           isActive(item.path) 
                             ? 'text-accent-600 font-semibold' 
                             : 'text-gray-700 hover:text-accent-600'
                         } transition-colors duration-200`}
                         onClick={() => {
                           setIsMobileMenuOpen(false);
                           // Ensure header is visible
                           setIsHeaderVisible(true);
                         }}
                       >
                        {item.name}
                      </Link>
                      
                      {/* Mobile Submenu */}
                      {item.submenu && (
                        <div className="ml-4 mt-2 space-y-2">
                          {item.submenu.map((subItem) => (
                                                         <Link
                               key={subItem.name}
                               to={subItem.path}
                               className="flex items-center space-x-3 py-2 text-gray-600 hover:text-accent-600 transition-colors duration-200"
                               onClick={() => {
                                 setIsMobileMenuOpen(false);
                                 // Ensure header is visible
                                 setIsHeaderVisible(true);
                               }}
                             >
                              <span className="text-lg">{subItem.icon}</span>
                              <span>{subItem.name}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <div className="pt-4 border-t border-gray-200 space-y-2">
                    {user ? (
                      // Mobile user menu
                      <>
                        <div className="py-2 border-b border-gray-100">
                          <p className="font-semibold text-gray-900">{user.fullName}</p>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                        <Link
                          to="/dashboard"
                          className="block py-2 text-gray-700 hover:text-accent-600 transition-colors duration-200"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Dashboard
                        </Link>
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsMobileMenuOpen(false);
                          }}
                          className="block w-full text-left py-2 text-gray-700 hover:text-accent-600 transition-colors duration-200"
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      // Mobile login/register buttons
                      <>
                        <Link
                          to="/login"
                          className="block py-2 text-gray-700 hover:text-accent-600 transition-colors duration-200"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Login
                        </Link>
                        <Link
                          to="/register"
                          className="block bg-gradient-to-r from-accent-500 to-accent-600 text-white px-6 py-3 rounded-lg text-center hover:from-accent-600 hover:to-accent-700 transition-all duration-300"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Get Started
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.header>
      )}
    </AnimatePresence>
  );
};

export default Header;